/**
 * Which pool this campaign draws from.
 *
 * Rendered as two tiles rather than two buttons because this is not a filter
 * change, it is a change of what every number on the screen counts: the register
 * mirror counts COMPANIES and the scanner leads count DOMAINS. Several companies
 * can list one domain and one company can own three, so "3 000" means a
 * different thing on either side of this control. Each tile therefore carries
 * its unit, and the selected one carries it in the accent colour, so the unit is
 * on screen at the moment the choice is made and not only in the preview
 * afterwards.
 */
import { Building2, Radar } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";

type Source = "register" | "scanLeads";

const ICONS: Record<Source, typeof Building2> = {
  register: Building2,
  scanLeads: Radar,
};

function Tile({
  source,
  active,
  disabled,
  onSelect,
}: {
  source: Source;
  active: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  const t = useT();
  const Icon = ICONS[source];
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "flex flex-1 items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors disabled:opacity-60",
        active
          ? "border-primary/50 bg-primary/5 ring-1 ring-inset ring-primary/20"
          : "border-border bg-background/40 hover:border-primary/30 hover:bg-accent/40",
      )}
    >
      <Icon className={cn("mt-0.5 size-4 shrink-0", active ? "text-primary" : "text-muted-foreground")} />
      <span className="min-w-0">
        <span className="block text-sm font-medium">{t(`marketing.source.${source}`)}</span>
        <span className={cn("mt-0.5 block text-[11px] leading-snug", active ? "text-primary/80" : "text-muted-foreground")}>
          {t(`marketing.source.${source}Unit`)}
        </span>
      </span>
    </button>
  );
}

export function SourceSwitch({
  value,
  onChange,
  disabled,
}: {
  value: Source;
  onChange: (v: Source) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <Tile source="register" active={value === "register"} disabled={disabled} onSelect={() => onChange("register")} />
      <Tile
        source="scanLeads"
        active={value === "scanLeads"}
        disabled={disabled}
        onSelect={() => onChange("scanLeads")}
      />
    </div>
  );
}
